/**
 * NetUsersGrowthChart
 *
 * Net Paying Users Growth chart showing new vs lost customers.
 */
import React from 'react';
import { Info, TrendingUp, Calendar, Activity } from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  ResponsiveContainer,
  Tooltip as RechartsTooltip
} from 'recharts';
import AnalyticsTooltip from './AnalyticsTooltip';

const NetUsersGrowthChart = ({ analyticsData, formatDate, formatDateLong }) => {
  const netData = analyticsData.netUsersTrends || [];
  
  // Calculate last 30 days stats
  const last30Days = netData.slice(-30);
  const added30Days = last30Days.reduce((sum, day) => sum + day.newPayingCustomers, 0);
  const netChange30Days = last30Days.reduce((sum, day) => sum + day.netChange, 0);
  
  // Calculate last 7 days stats
  const last7Days = netData.slice(-7);
  const netChange7Days = last7Days.reduce((sum, day) => sum + day.netChange, 0);
  
  // Calculate growth rate
  const currentPayingCustomers = analyticsData.summary?.payingCustomers || 0;
  const startingCustomers = Math.max(currentPayingCustomers - netChange30Days, 1);
  const growthRate = (netChange30Days / startingCustomers) * 100;

  return (
    <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Net Paying Users Growth
        </h3>
        <AnalyticsTooltip content="Daily net change in paying customers (new paying customers minus lost customers). Shows your actual customer growth after accounting for churn. Only includes paying customers, not trial users." id="net-users-tooltip">
          <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
        </AnalyticsTooltip>
      </div>
      
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={netData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
          <XAxis 
            dataKey="date" 
            tickFormatter={formatDate}
            stroke="#6B7280"
            fontSize={12}
            interval="preserveStartEnd"
            minTickGap={30}
          />
          <YAxis 
            stroke="#6B7280"
            fontSize={12}
            allowDecimals={false}
          />
          <RechartsTooltip 
            labelFormatter={(value) => formatDateLong(value)}
            formatter={(value, name) => {
              const formatMap = {
                'newPayingCustomers': 'New Customers',
                'lostUsers': 'Lost Customers', 
                'netChange': 'Net Change'
              };
              return [value, formatMap[name] || name];
            }}
            contentStyle={{
              backgroundColor: '#1F2937',
              border: '1px solid #374151',
              borderRadius: '8px',
              color: '#F9FAFB'
            }}
          />
          <Bar 
            dataKey="newPayingCustomers" 
            fill="#10B981"
            radius={[2, 2, 0, 0]}
            name="newPayingCustomers"
          />
          <Bar 
            dataKey="lostUsers" 
            fill="#EF4444"
            radius={[2, 2, 0, 0]}
            name="lostUsers"
          />
          <Bar 
            dataKey="netChange" 
            fill="#3B82F6"
            radius={[2, 2, 0, 0]}
            name="netChange"
          />
        </BarChart>
      </ResponsiveContainer>
      
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Users Added This Month */}
        <div className="bg-green-50 dark:bg-transparent backdrop-blur-md rounded-lg p-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-lg">
              <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-green-700 dark:text-green-300 font-medium">Added Last 30 Days</p>
              <p className="text-lg font-bold text-green-900 dark:text-green-100">
                {added30Days} users
              </p>
              <p className="text-xs text-green-600 dark:text-green-400">New paying customers</p>
            </div>
          </div>
        </div>

        {/* Net Users This Week */}
        <div className="bg-blue-50 dark:bg-transparent backdrop-blur-md rounded-lg p-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
              <Calendar className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">Net Last 7 Days</p>
              <p className="text-lg font-bold text-blue-900 dark:text-blue-100">
                {netChange7Days >= 0 ? '+' : ''}{netChange7Days} users
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400">Paying customers only</p>
            </div>
          </div>
        </div>

        {/* Net Users This Month */}
        <div className="bg-purple-50 dark:bg-transparent backdrop-blur-md rounded-lg p-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
              <TrendingUp className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-purple-700 dark:text-purple-300 font-medium">Net Last 30 Days</p>
              <p className="text-lg font-bold text-purple-900 dark:text-purple-100">
                {netChange30Days >= 0 ? '+' : ''}{netChange30Days} users
              </p>
              <p className="text-xs text-purple-600 dark:text-purple-400">Paying customers only</p>
            </div>
          </div>
        </div>

        {/* Growth Rate */}
        <div className="bg-indigo-50 dark:bg-transparent backdrop-blur-md rounded-lg p-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/40 rounded-lg">
              <Activity className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <p className="text-sm text-indigo-700 dark:text-indigo-300 font-medium">Monthly Growth Rate</p>
              <p className="text-lg font-bold text-indigo-900 dark:text-indigo-100">
                {netData.length >= 30 ? `${growthRate >= 0 ? '+' : ''}${growthRate.toFixed(1)}%` : '0%'}
              </p>
              <p className="text-xs text-indigo-600 dark:text-indigo-400">Customer growth</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 p-4 bg-gray-50 dark:bg-transparent backdrop-blur-md rounded-lg">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          <strong>💡 Growth insight:</strong> Track your net user growth to understand true business momentum. 
          Positive net growth means you're acquiring more paying customers than you're losing. 
          Use monthly growth rates to project future revenue and plan capacity.
        </p>
      </div>
    </div>
  );
};

export default NetUsersGrowthChart;


